import { Auth0Provider } from "@auth0/auth0-react";
import { AppInsightsContext } from "@microsoft/applicationinsights-react-js";
import PropTypes from "prop-types";
import React from "react";
import { I18nextProvider } from "react-i18next";
import { HashRouter } from "react-router-dom";
import { PersistGate } from "redux-persist/integration/react";
import { ai, reactPlugin } from "../applicationInsights";
import reportWebVitals from "../reportWebVitals";
import BaseApp from "./baseApp";
import { LoginStatus } from "./constants/loginStatus";
import { getBaseConfig } from "./helpers/baseConfig";
import useLogin from "./hooks/useLogin";
import { getI18nConfig } from "./languages";
import Logger from "./logger";
import SFProvider from "./provider";
import { layoutActions } from "./redux/reducers/layoutReducer";
import { setAvatar } from "./redux/reducers/sessionReducer";
import { toastActions } from "./redux/reducers/toastReducer";
import { getStore, history } from "./redux/store";
import Auth0AuthenticationService from "./services/auth0AuthenticationService";
import base, { formatUrlParams } from "./services/base";
import { baseContainer } from "./services/baseContainerInstance";
import CookiesService from "./services/cookiesService";
import MSGraphService from "./services/msGraphService";

const auth0AuthenticationService = new Auth0AuthenticationService();
const cookiesService = new CookiesService();
const activeDirectoryService = new MSGraphService();

const BaseProvider = ({ children, config }) => {
  const {
    translations,
    translationsPath,
    reducers,
    themeProvider,
    appInsights,
    persistStoreKey,
    ...otherConfig
  } = getBaseConfig(config);

  const { store, persistor } = getStore(reducers, persistStoreKey);

  ai.initialize(appInsights);

  baseContainer.setConfig(otherConfig);
  baseContainer.setService(auth0AuthenticationService);
  baseContainer.setCookiesService(cookiesService);
  baseContainer.setActiveDirectoryService(activeDirectoryService);

  return (
    <>
      <AppInsightsContext.Provider value={reactPlugin}>
        <Auth0Provider
          domain={otherConfig.auth0Config.domain}
          clientId={otherConfig.auth0Config.clientId}
          //cacheLocation="localstorage"
          authorizationParams={{
             
            redirect_uri: `${window.location.origin}`,
            audience: otherConfig.auth0Config.audience,
            scope: otherConfig.auth0Config.scope,
          }}
        >
          <React.StrictMode>
            <SFProvider
              history={history}
              store={store}
              persistor={persistor}
              auth0Config={otherConfig.auth0Config}
              themeProvider={themeProvider}
            >
              <PersistGate persistor={persistor}>
                <HashRouter>
                  <I18nextProvider i18n={getI18nConfig(translations, { translationsPath })}>
                    {children}
                  </I18nextProvider>
                </HashRouter>
              </PersistGate>
            </SFProvider>
          </React.StrictMode>
        </Auth0Provider>
      </AppInsightsContext.Provider>
    </>
  );
};

BaseProvider.propTypes = {
  children: PropTypes.node,
  config: PropTypes.shape({
    translations: PropTypes.object,
    translationsPath: PropTypes.string,
    reducers: PropTypes.object,
    claimTypes: PropTypes.shape({
      tenant: PropTypes.string,
      securityHash: PropTypes.string,
      commonPermissions: PropTypes.string,
      tenantPermissions: PropTypes.string,
    }),
    apiEndpoints: PropTypes.shape({
      base: PropTypes.string.isRequired,
      identityLogout: PropTypes.string,
      identityCurrent: PropTypes.string,
      identityConfig: PropTypes.string,
      identityTenants: PropTypes.string,
      identityAvatar: PropTypes.string,
      identityTenantToken: PropTypes.string,
      nonceToken: PropTypes.string,
      maintenance: PropTypes.string,
      tenantConfig: PropTypes.string,
    }).isRequired,
    graphConfig: PropTypes.shape({
      graphEndpoint: PropTypes.string,
      userPhotosApi: PropTypes.string,
      userPhotoByIdOrPrincipalName: PropTypes.string,
      usersApi: PropTypes.string,
      scope: PropTypes.string,
    }),
    forceTenant: PropTypes.bool,
    themeProvider: PropTypes.any,
    appInsights: PropTypes.shape({
      enable: PropTypes.bool,
      instrumentationKey: PropTypes.string,
    }),
    persistStoreKey: PropTypes.string,
  }).isRequired,
};

export default BaseProvider;

export {
  base,
  BaseApp,
  baseContainer,
  formatUrlParams,
  layoutActions,
  Logger,
  LoginStatus,
  setAvatar,
  toastActions,
  useLogin,
};

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
